/**
 * AADE Insights Dashboard
 *
 * Displays insights from AADE with filtering, sorting, and actions.
 */

import React, { useState, useEffect } from 'react';
import { Insight } from '../../services/aadeService';
import { aadeGatewayService } from '../../services/aadeGatewayService';
import { aadeUiUtils } from '../../services/aadeUiUtils';
import {
  AlertCircle as FiAlertCircle,
  CheckCircle2 as FiCheckCircle,
  RefreshCw as FiRefreshCw,
  Filter as FiFilter,
  TrendingUp as FiTrendingUp,
  Shield as FiShield,
  Activity as FiActivity,
} from 'lucide-react';

const AADEInsights: React.FC = () => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [showActionableOnly, setShowActionableOnly] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string>('');

  useEffect(() => {
    fetchInsights();
  }, [filterType]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      setError(null);

      const insightType = filterType === 'all' ? undefined : filterType;
      const response = await aadeGatewayService.getLatestInsights(insightType, 50);

      setInsights(response.insights);
      setLastSyncTime(new Date().toLocaleString());
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch insights');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (insightId: string) => {
    try {
      await aadeGatewayService.acknowledgeInsight(insightId);
      // Update local state
      setInsights(
        insights.map((insight) =>
          insight.id === insightId
            ? { ...insight, acknowledged_at: new Date().toISOString() }
            : insight
        )
      );
    } catch (err: any) {
      alert('Failed to acknowledge insight: ' + err.message);
    }
  };

  const handleAction = async (insightId: string) => {
    const action = prompt('What action did you take?');
    if (!action) return;

    const notes = prompt('Any additional notes?');

    try {
      await aadeGatewayService.recordInsightAction(insightId, action, notes || undefined);
      alert('Action recorded successfully');
      fetchInsights(); // Refresh
    } catch (err: any) {
      alert('Failed to record action: ' + err.message);
    }
  };

  const filteredInsights = insights
    .filter((insight) => {
      if (filterPriority !== 'all' && insight.priority !== filterPriority) {
        return false;
      }
      if (showActionableOnly && !insight.is_actionable) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      // Sort by priority (critical > high > medium > low)
      const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });

  const getInsightIcon = (type: string) => {
    const icons: Record<string, any> = {
      partner_risk: <FiShield className="text-orange-500" />,
      portfolio_health: <FiActivity className="text-green-500" />,
      anomaly_detection: <FiAlertCircle className="text-red-500" />,
      profit_forecast: <FiTrendingUp className="text-blue-500" />,
    };
    return icons[type] || <FiActivity className="text-gray-500" />;
  };

  const stats = {
    total: insights.length,
    actionable: insights.filter((i) => i.is_actionable).length,
    critical: insights.filter((i) => i.priority === 'critical').length,
    unacknowledged: insights.filter((i) => !i.acknowledged_at).length,
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">AADE Insights</h1>
            <p className="text-gray-600 mt-2">
              AI-powered analytics and recommendations
              {lastSyncTime && (
                <span className="ml-2 text-sm">• Last updated: {lastSyncTime}</span>
              )}
            </p>
          </div>
          <button
            onClick={fetchInsights}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-gray-500 text-sm mb-2">Total Insights</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-gray-500 text-sm mb-2">Actionable</div>
            <div className="text-3xl font-bold text-orange-600">{stats.actionable}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-gray-500 text-sm mb-2">Critical Priority</div>
            <div className="text-3xl font-bold text-red-600">{stats.critical}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-gray-500 text-sm mb-2">Unacknowledged</div>
            <div className="text-3xl font-bold text-yellow-600">{stats.unacknowledged}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <FiFilter />
              <span className="text-sm font-medium">Filters:</span>
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Types</option>
              <option value="partner_risk">Partner Risk</option>
              <option value="portfolio_health">Portfolio Health</option>
              <option value="anomaly_detection">Anomaly Detection</option>
              <option value="profit_forecast">Profit Forecast</option>
              <option value="performance_analysis">Performance Analysis</option>
            </select>

            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showActionableOnly}
                onChange={(e) => setShowActionableOnly(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Actionable only</span>
            </label>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <FiRefreshCw className="animate-spin text-4xl text-blue-600" />
          </div>
        )}

        {/* Insights List */}
        {!loading && filteredInsights.length === 0 && (
          <div className="bg-white p-12 rounded-lg shadow text-center">
            <FiCheckCircle className="mx-auto text-6xl text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Insights Found
            </h3>
            <p className="text-gray-600">
              {insights.length === 0
                ? 'No insights available yet. Trigger a sync to generate insights.'
                : 'No insights match your current filters.'}
            </p>
          </div>
        )}

        {!loading && filteredInsights.length > 0 && (
          <div className="space-y-4">
            {filteredInsights.map((insight) => (
              <div
                key={insight.id}
                className={`bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow ${
                  !insight.acknowledged_at ? 'border-l-4 border-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="text-2xl mt-1">{getInsightIcon(insight.type)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {insight.title}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${aadeUiUtils.getInsightPriorityBadgeColor(
                            insight.priority
                          )}`}
                        >
                          {insight.priority}
                        </span>
                        {insight.is_actionable && (
                          <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
                            Action Required
                          </span>
                        )}
                        {insight.acknowledged_at && (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            Acknowledged
                          </span>
                        )}
                      </div>

                      <p className="text-gray-600 mb-3">{insight.summary}</p>

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Type: {insight.type.replace(/_/g, ' ')}</span>
                        {insight.confidence_score && (
                          <span>
                            Confidence: {aadeUiUtils.formatConfidence(insight.confidence_score)}
                          </span>
                        )}
                        <span>Generated: {new Date(insight.generated_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    {!insight.acknowledged_at && (
                      <button
                        onClick={() => handleAcknowledge(insight.id)}
                        className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Acknowledge
                      </button>
                    )}
                    {insight.is_actionable && (
                      <button
                        onClick={() => handleAction(insight.id)}
                        className="px-3 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                      >
                        Record Action
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AADEInsights;
